import { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { HelpPanel } from '../components/ui/help-panel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import OCRProviderPanel from '../components/ocr/OCRProviderPanel';
import SmartActionsBar from '../components/ocr/SmartActionsBar';
import InlineAskAI from '../components/ocr/InlineAskAI';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  Plus,
  Trash2,
  RefreshCcw,
  Eye,
  Download,
  Save,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Sparkles,
  ScanLine,
  ChevronDown,
  Clock,
  CheckCircle,
  MoreHorizontal,
  ArrowRight,
  Folder,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  metaFor,
  SMART_ACTION_DESTINATION,
  formatCurrency,
  FILING_DESTINATIONS,
} from '../components/ocr/ocrConstants';
import { ConfidenceBadge, OriginBadge, OCRField, LineItemRow } from '../components/ocr/OCRFieldComponents';
import { DocumentViewer } from '../components/ocr/DocumentViewer';
import { UploadStep } from '../components/ocr/UploadStep';

const API_URL = process.env.REACT_APP_BACKEND_URL;


// Main OCR Review Page
export default function InvoiceOCR() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get params from URL. `documentType` is now only a hint for the upload
  // step — the real source of truth post-extraction is `job.doc_type`
  // detected by the backend Smart-Actions detector.
  const documentType = searchParams.get('type') || 'bill';
  const existingJobId = searchParams.get('job');
  
  const [step, setStep] = useState(existingJobId ? 'review' : 'upload');
  const [jobId, setJobId] = useState(existingJobId);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  
  // Extracted data state
  const [extraction, setExtraction] = useState({
    vendor_or_customer: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    line_items: [],
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0
  });
  
  // Confidence tracking
  const [confidences, setConfidences] = useState({});
  // Track which fields were edited by a human post-extraction. Keyed by
  // field name (e.g. "vendor_or_customer") for top-level fields and
  // "line_items.{i}.{key}" for line-item cells. Drives the AI/Edited
  // badge on each field for the audit trail.
  const [editedFields, setEditedFields] = useState({});
  const [confirmedFields, setConfirmedFields] = useState({});
  
  // Duplicate warning state
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  // Phase 3 — inline Ask AI panel toggle (closes by default; opens when
  // user clicks the "Ask AI about this" smart-action chip).
  const [showAskAI, setShowAskAI] = useState(false);
  // Tracks the in-flight file/save call so the footer button + smart
  // actions show a spinner without double-submitting.
  const [filing, setFiling] = useState(false);
  // Smart Routing Memory — when AI Buck has seen this vendor + doc_type
  // before, surface a one-click "File to X like last time" suggestion.
  const [routingSuggestion, setRoutingSuggestion] = useState(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  
  // Effective doc-type: prefer the backend-detected `job.doc_type`, fall
  // back to the URL hint while the upload is still pending.
  const effectiveDocType = job?.doc_type || documentType || 'unknown';
  const meta = metaFor(effectiveDocType);
  
  // Load job data
  const loadJob = useCallback(async () => {
    if (!jobId) return;
    
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/ocr/${jobId}`);
      
      if (!response.ok) {
        throw new Error('Failed to load OCR job');
      }
      
      const data = await response.json();
      setJob(data);
      
      // Parse extraction data
      const extractionData = data.extraction_data || {};
      const newConfidences = {};
      const newEdited = {};
      
      const getValue = (field) => {
        const fieldData = extractionData[field];
        if (fieldData) {
          newConfidences[field] = fieldData.confidence || 1;
          // user_corrected flag persisted by PATCH /api/ocr/{job_id} when
          // a teammate previously edited this field. Keeps the badge sticky
          // across reloads.
          if (fieldData.user_corrected) newEdited[field] = true;
          return fieldData.value || '';
        }
        return '';
      };
      
      setExtraction({
        vendor_or_customer: getValue('vendor_or_customer'),
        invoice_number: getValue('invoice_number'),
        invoice_date: getValue('invoice_date'),
        due_date: getValue('due_date'),
        line_items: extractionData.line_items || [],
        subtotal: parseFloat(getValue('subtotal')) || 0,
        tax_amount: parseFloat(getValue('tax_amount')) || 0,
        total_amount: parseFloat(getValue('total_amount')) || 0
      });
      
      setConfidences(newConfidences);
      setEditedFields(newEdited);
      setDuplicateWarning(data.duplicate_warning);
      
      // Check if needs review confirmation
      if (data.requires_review) {
        setStep('review');
      }
      
    } catch (error) {
      console.error('Failed to load job:', error);
      toast.error('Failed to load OCR data');
    } finally {
      setLoading(false);
    }
  }, [jobId]);
  
  useEffect(() => {
    if (jobId) {
      loadJob();
    }
  }, [jobId, loadJob]);

  // Phase 1 async polling — once we have a job, keep refreshing every 2 s
  // until the OCR job reaches a terminal state. Without this the review
  // form would stay empty because the upload endpoint now returns before
  // extraction finishes.
  useEffect(() => {
    if (!job || !jobId) return;
    const terminal = ['completed', 'requires_review', 'failed', 'duplicate_file'];
    if (terminal.includes(job.status)) return;
    const t = setTimeout(() => {
      loadJob();
    }, 2000);
    return () => clearTimeout(t);
  }, [job, jobId, loadJob]);

  // Smart Routing Memory — fetch the suggestion once the OCR job has a
  // detected doc_type. Re-fetch when doc_type changes (e.g. heuristic
  // re-runs after a re-OCR).
  useEffect(() => {
    if (!jobId || !job || !job.doc_type) return;
    if (job.filed) return; // Already filed — no suggestion needed
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/routing-suggestion`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setRoutingSuggestion(data.match || null);
      } catch {
        // Silent — suggestion is a convenience.
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, job?.doc_type, job?.filed]);

  // Handle upload complete
  const handleUploadComplete = (newJobId) => {
    setJobId(newJobId);
    setStep('review');
  };
  
  // Update field
  const updateField = (field, value) => {
    setExtraction(prev => ({ ...prev, [field]: value }));
    // Mark as edited so the OriginBadge flips from "AI" to "✓ Edited".
    setEditedFields(prev => ({ ...prev, [field]: true }));
  };
  
  // Confirm low-confidence field
  const confirmField = (field) => {
    setConfirmedFields(prev => ({ ...prev, [field]: true }));
  };
  
  // Update line item
  const updateLineItem = (index, field, value) => {
    setExtraction(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Auto-calculate amount if quantity or unit_price changes
      if (field === 'quantity' || field === 'unit_price') {
        newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
      }
      
      return { ...prev, line_items: newItems };
    });
  };
  
  // Add line item
  const addLineItem = () => {
    setExtraction(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        { description: '', quantity: 1, unit_price: 0, amount: 0 }
      ]
    }));
  };
  
  // Remove line item
  const removeLineItem = (index) => {
    setExtraction(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };
  
  // Calculate totals
  const calculatedSubtotal = extraction.line_items.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0), 0
  );
  const calculatedTotal = calculatedSubtotal + (parseFloat(extraction.tax_amount) || 0);
  const hasVariance = Math.abs(calculatedTotal - extraction.total_amount) > 0.01;
  
  // Check if all low-confidence fields are confirmed
  const hasUnconfirmedLowConfidence = Object.entries(confidences).some(
    ([field, conf]) => conf < 0.70 && !confirmedFields[field]
  );
  
  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/api/ocr/${jobId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendor_or_customer: extraction.vendor_or_customer,
          invoice_number: extraction.invoice_number,
          invoice_date: extraction.invoice_date,
          due_date: extraction.due_date,
          line_items: extraction.line_items.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            amount: item.amount
          })),
          subtotal: calculatedSubtotal,
          tax_amount: extraction.tax_amount,
          total_amount: calculatedTotal
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save changes');
      }
      
      toast.success('Changes saved');
      await loadJob();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };
  
  // File a non-bill/invoice document to the right destination. Used by
  // both the footer primary action (when `meta.primaryAction.kind === 'file'`)
  // and by the Smart Actions chips for filing-type actions.
  const handleFileDocument = useCallback(async (destination, opts = {}) => {
    if (!jobId) return;
    setFiling(true);
    try {
      const response = await apiFetch(`${API_URL}/api/ocr/${jobId}/file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to file document');
      }
      const result = await response.json();
      toast.success(result.message || `Filed to ${destination.replace('_', ' ')}`);
      if (!opts.skipNavigate && result.navigate_to) {
        navigate(result.navigate_to);
      }
      return result;
    } catch (error) {
      console.error('File error:', error);
      toast.error(error.message || 'Failed to file document');
    } finally {
      setFiling(false);
    }
  }, [jobId, navigate]);

  // Create draft (bill or invoice flow). Kept separate so we can keep
  // the duplicate-detection dialog on the AP/AR path only.
  const handleCreateDraft = async (ignoreWarning = false) => {
    if (duplicateWarning && !ignoreWarning) {
      setShowDuplicateDialog(true);
      return;
    }
    
    if (hasUnconfirmedLowConfidence) {
      toast.error('Please confirm all low-confidence fields before creating draft');
      return;
    }
    
    setCreatingDraft(true);
    try {
      // Save changes first
      await handleSave();
      
      // Create draft
      const response = await apiFetch(`${API_URL}/api/ocr/${jobId}/create-draft`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create draft');
      }
      
      const result = await response.json();
      toast.success(`Draft ${result.document_type} created`);
      
      // Navigate to the appropriate page
      if (result.document_type === 'bill') {
        navigate('/bills');
      } else {
        navigate('/invoices');
      }
    } catch (error) {
      console.error('Create draft error:', error);
      toast.error(error.message || 'Failed to create draft');
    } finally {
      setCreatingDraft(false);
      setShowDuplicateDialog(false);
    }
  };

  // Dispatch the footer "primary" CTA based on the detected doc type.
  const handlePrimaryAction = async () => {
    const action = meta.primaryAction;
    if (action.kind === 'draft') {
      await handleCreateDraft(false);
    } else if (action.kind === 'file') {
      await handleFileDocument(action.destination);
    }
  };
  
  // Back to upload
  const handleBackToUpload = () => {
    setJobId(null);
    setJob(null);
    setStep('upload');
    setExtraction({
      vendor_or_customer: '',
      invoice_number: '',
      invoice_date: '',
      due_date: '',
      line_items: [],
      subtotal: 0,
      tax_amount: 0,
      total_amount: 0
    });
    setConfidences({});
    setConfirmedFields({});
    setDuplicateWarning(null);
  };
  
  // Help panel content
  const helpSections = [
    {
      heading: "What is OCR?",
      content: "AI Buck uses OCR (Optical Character Recognition) to automatically extract text and data from your uploaded documents. This saves time on manual data entry."
    },
    {
      heading: "AI Buck Confidence Scores",
      content: (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-800 text-xs">High (85%+)</Badge>
            <span>AI Buck is confident—quick review sufficient</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-100 text-amber-800 text-xs">Medium (70-84%)</Badge>
            <span>AI Buck suggests reviewing before accepting</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-rose-100 text-rose-800 text-xs">Low (&lt;70%)</Badge>
            <span>AI Buck needs your input—manual verification required</span>
          </div>
        </div>
      )
    },
    {
      heading: "Line Items",
      content: "You can add, edit, or remove line items. Totals are automatically recalculated when you make changes."
    },
    {
      heading: "Creating Drafts",
      content: "When you save as draft, AI Buck creates a draft bill or invoice that must go through your normal approval workflow before posting. AI Buck never auto-posts."
    }
  ];
  
  const helpTips = [
    "Review fields highlighted in amber—AI Buck flagged them for verification",
    "Click 'Confirm' on low-confidence fields after verifying them",
    "The original document is shown on the left for reference",
    "Drafts never auto-post—AI Buck requires manual approval"
  ];
  
  const helpWarnings = [
    "AI Buck requires confirmation of low-confidence fields before creating a draft",
    "Total variance warnings indicate AI Buck detected a mismatch between line items and the document total"
  ];
  
  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select a workspace</p>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col" data-testid="invoice-ocr-page">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              // Back goes to the natural list view for this doc type.
              if (effectiveDocType === 'bill') navigate('/bills');
              else if (effectiveDocType === 'invoice') navigate('/invoices');
              else if (effectiveDocType === 'bank_statement') navigate('/banking');
              else if (effectiveDocType === 'receipt') navigate('/documents?folder=receipts');
              else if (effectiveDocType === 'tax_return') navigate('/documents?folder=tax');
              else navigate('/documents');
            }}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              {step === 'upload' ? (
                <>
                  <ScanLine className="w-5 h-5 text-violet-600" />
                  Document Scanner
                </>
              ) : (
                <>Review {meta.label}</>
              )}
            </h1>
            <p className="text-sm text-muted-foreground">
              {step === 'upload' 
                ? 'AI Buck reads any financial document and lights up Smart Actions for each.'
                : 'Verify extracted data and save to the right place'
              }
            </p>
          </div>
        </div>
        <HelpPanel
          title="Document Scanner Help"
          description="Learn how to use the document scanner"
          sections={helpSections}
          tips={helpTips}
          warnings={helpWarnings}
        />
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {step === 'upload' ? (
          <div className="h-full flex items-center justify-center p-6">
            <UploadStep 
              documentType={documentType} 
              onUploadComplete={handleUploadComplete}
            />
          </div>
        ) : loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
              <p className="mt-4 text-muted-foreground">Loading extracted data...</p>
            </div>
          </div>
        ) : (
          <div className="h-full grid grid-cols-2 divide-x">
            {/* Left: Document Viewer */}
            <div className="h-full overflow-hidden">
              <DocumentViewer 
                jobId={jobId}
                filename={job?.filename}
                contentType={job?.file_content_type}
              />
            </div>
            
            {/* Right: Extracted Data Form */}
            <div className="h-full flex flex-col">
              <ScrollArea className="flex-1">
                <div className="p-6 space-y-6">
                  {/* Async scanning banner — shows while AI Buck is still
                      reading the document. Polling keeps the form fresh. */}
                  {job && ['pending', 'processing'].includes(job.status) && (
                    <Alert className="bg-violet-50 border-violet-200 text-violet-900 dark:bg-violet-950/30 dark:border-violet-800 dark:text-violet-200" data-testid="ocr-scanning-banner">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <AlertTitle>AI Buck is reading your document…</AlertTitle>
                      <AlertDescription>
                        Extraction usually finishes in 10–30 seconds. The form will populate automatically.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Phase 2: Smart Actions — surfaces relevant next steps
                      once extraction is done (e.g., "Save as bill", "Pay
                      now", "Check duplicate"). Phase 3 wires "Ask AI" to
                      the inline chat panel; Phase 4 deep-links tax-return
                      "Find missed deductions" to the savings-report page. */}
                  {/* Smart Routing Memory banner — surface a one-click
                      suggestion when AI Buck has seen this (vendor, doc_type)
                      filed before. Renders ABOVE Smart Actions so the user
                      sees it first. Dismissable; ignored once the doc is
                      filed (the polling effect won't re-fetch). */}
                  {routingSuggestion && !suggestionDismissed && (
                    <Alert
                      className="border-violet-300 dark:border-violet-800 bg-gradient-to-r from-violet-50 to-fuchsia-50 dark:from-violet-950/30 dark:to-fuchsia-950/30"
                      data-testid="routing-suggestion-banner"
                    >
                      <Sparkles className="w-4 h-4 text-violet-600" />
                      <AlertTitle className="text-violet-900 dark:text-violet-200">
                        AI Buck noticed a pattern
                      </AlertTitle>
                      <AlertDescription className="text-violet-800 dark:text-violet-300">
                        <div className="mb-2">
                          You&apos;ve filed <span className="font-semibold">{routingSuggestion.hit_count}</span>{' '}
                          {routingSuggestion.hit_count === 1 ? 'scan' : 'scans'} from{' '}
                          <span className="font-semibold">{routingSuggestion.vendor_label}</span>{' '}
                          to{' '}
                          <span className="font-semibold">
                            {(FILING_DESTINATIONS.find((d) => d.value === routingSuggestion.destination) || {}).shortLabel || routingSuggestion.destination}
                          </span>{' '}
                          before. File this one there too?
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            disabled={filing}
                            onClick={async () => {
                              await handleFileDocument(routingSuggestion.destination);
                              setSuggestionDismissed(true);
                            }}
                            data-testid="routing-suggestion-confirm"
                          >
                            {filing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1.5" />}
                            File to {(FILING_DESTINATIONS.find((d) => d.value === routingSuggestion.destination) || {}).shortLabel || routingSuggestion.destination}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-violet-700 dark:text-violet-300"
                            onClick={() => setSuggestionDismissed(true)}
                            data-testid="routing-suggestion-dismiss"
                          >
                            Not this time
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {job && ['completed', 'requires_review'].includes(job.status) && (
                    <SmartActionsBar
                      jobId={jobId}
                      onAction={(action, data) => {
                        if (action.id === 'ask_ai') {
                          setShowAskAI(true);
                          return;
                        }
                        if (action.id === 'find_deductions' || action.id === 'check_errors' || action.id === 'compare_yoy') {
                          // Compare-to-last-year deep-links to the YoY
                          // section so the user lands on the comparison,
                          // not the headline. The other two chips land
                          // on the top of the report (deductions/errors).
                          const hash = action.id === 'compare_yoy' ? '#yoy-swings' : '';
                          navigate(`/ocr/${jobId}/tax-report${hash}`);
                          return;
                        }
                        // Filing-style actions route through the new
                        // generic /file endpoint and navigate to the
                        // destination listing page.
                        const dest = SMART_ACTION_DESTINATION[action.id];
                        if (dest) {
                          handleFileDocument(dest);
                          return;
                        }
                        // Bill-only actions (kept inside the existing
                        // draft flow until we wire dedicated UIs).
                        if (action.id === 'schedule_pay') {
                          handleCreateDraft(false);
                          return;
                        }
                        toast.success(`Action queued: ${action.label}`);
                      }}
                    />
                  )}

                  {/* Phase 3 — inline Ask AI chat panel for this OCR job */}
                  {showAskAI && job && ['completed', 'requires_review'].includes(job.status) && (
                    <InlineAskAI
                      jobId={jobId}
                      docType={job.doc_type}
                      onClose={() => setShowAskAI(false)}
                    />
                  )}

                  {/* Confidence Summary */}
                  {job && (
                    <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">AI Buck Confidence:</span>
                          <ConfidenceBadge confidence={job.avg_confidence || 0} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Minimum field confidence: {Math.round((job.min_confidence || 0) * 100)}%
                        </p>
                      </div>
                      {job.requires_review && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          AI Buck Needs Review
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* What AI Buck read — provider + per-field confidence breakdown */}
                  <OCRProviderPanel job={job} extraction={job?.extraction_data} />
                  
                  {/* Duplicate Warning */}
                  {duplicateWarning && (
                    <Alert variant="destructive" className="bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
                      <AlertTriangle className="w-4 h-4" />
                      <AlertTitle>AI Buck Alert: Potential Duplicate</AlertTitle>
                      <AlertDescription>
                        {duplicateWarning.message}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Variance Warning */}
                  {hasVariance && extraction.total_amount > 0 && (
                    <Alert variant="destructive" className="bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-200">
                      <AlertCircle className="w-4 h-4" />
                      <AlertTitle>AI Buck Alert: Total Mismatch</AlertTitle>
                      <AlertDescription>
                        AI Buck detected a variance: Line items total ({formatCurrency(calculatedTotal)}) does not match the extracted total ({formatCurrency(extraction.total_amount)}). 
                        Please verify the line items.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Basic Info — only shown for doc types that have a clear counterparty */}
                  {meta.showContact && (
                    <Card>
                      <CardHeader className="pb-4">
                        <CardTitle className="text-base">
                          {meta.contactLabel} Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <OCRField
                          label={`${meta.contactLabel} Name`}
                          value={extraction.vendor_or_customer}
                          confidence={confidences.vendor_or_customer}
                          onChange={(v) => updateField('vendor_or_customer', v)}
                          confirmed={confirmedFields.vendor_or_customer}
                          onConfirm={() => confirmField('vendor_or_customer')}
                          placeholder={`Enter ${meta.contactLabel.toLowerCase()} name`}
                          edited={editedFields.vendor_or_customer}
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <OCRField
                            label={effectiveDocType === 'bill' || effectiveDocType === 'invoice' ? 'Invoice Number' : 'Reference No.'}
                            value={extraction.invoice_number}
                            confidence={confidences.invoice_number}
                            onChange={(v) => updateField('invoice_number', v)}
                            confirmed={confirmedFields.invoice_number}
                            onConfirm={() => confirmField('invoice_number')}
                            placeholder="REF-001"
                            edited={editedFields.invoice_number}
                          />
                          <OCRField
                            label="Document Date"
                            value={extraction.invoice_date}
                            confidence={confidences.invoice_date}
                            onChange={(v) => updateField('invoice_date', v)}
                            confirmed={confirmedFields.invoice_date}
                            onConfirm={() => confirmField('invoice_date')}
                            type="date"
                            edited={editedFields.invoice_date}
                          />
                        </div>
                        {(effectiveDocType === 'bill' || effectiveDocType === 'invoice') && (
                          <OCRField
                            label="Due Date"
                            value={extraction.due_date}
                            confidence={confidences.due_date}
                            onChange={(v) => updateField('due_date', v)}
                            confirmed={confirmedFields.due_date}
                            onConfirm={() => confirmField('due_date')}
                            type="date"
                            edited={editedFields.due_date}
                          />
                        )}
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Line Items — only relevant for transactional docs */}
                  {meta.showLineItems && (
                    <Card>
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Line Items</CardTitle>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={addLineItem}
                            data-testid="add-line-item-btn"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Item
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {extraction.line_items.length === 0 ? (
                          <div className="text-center py-6 text-muted-foreground">
                            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No line items</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              onClick={addLineItem}
                            >
                              Add First Item
                            </Button>
                          </div>
                        ) : (
                          extraction.line_items.map((item, index) => (
                            <LineItemRow
                              key={index}
                              item={item}
                              index={index}
                              onChange={updateLineItem}
                              onRemove={removeLineItem}
                              canRemove={extraction.line_items.length > 1}
                            />
                          ))
                        )}
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Totals — only relevant for transactional docs */}
                  {meta.showTotals && (
                    <Card>
                      <CardHeader className="pb-4">
                        <CardTitle className="text-base">Totals</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="font-mono font-medium">{formatCurrency(calculatedSubtotal)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="flex-1 text-sm text-muted-foreground">Tax</Label>
                          <Input
                            type="number"
                            value={extraction.tax_amount || 0}
                            onChange={(e) => updateField('tax_amount', parseFloat(e.target.value) || 0)}
                            className="w-32 text-right font-mono"
                            min="0"
                            step="0.01"
                            data-testid="tax-amount-input"
                          />
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between text-base font-semibold">
                          <span>Total</span>
                          <span className={cn(
                            "font-mono",
                            hasVariance && extraction.total_amount > 0 && "text-rose-600"
                          )}>
                            {formatCurrency(calculatedTotal)}
                          </span>
                        </div>
                        {hasVariance && extraction.total_amount > 0 && (
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Extracted total</span>
                            <span className="font-mono">{formatCurrency(extraction.total_amount)}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* For non-transactional docs (tax return, bank statement,
                      contract, W-9) we surface a friendly summary card
                      pointing users to the appropriate action. */}
                  {!meta.showLineItems && !meta.showTotals && job && ['completed', 'requires_review'].includes(job.status) && (
                    <Card data-testid="ocr-non-transactional-summary">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileCheck className="w-4 h-4 text-violet-600" />
                          {meta.label} ready to file
                        </CardTitle>
                        <CardDescription>
                          AI Buck has finished reading. Use a Smart Action above,
                          or click <span className="font-medium">{meta.primaryAction.label}</span> to save this document where it belongs.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  )}
                </div>
              </ScrollArea>
              
              {/* Footer Actions */}
              <div className="border-t p-4 bg-background">
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    onClick={handleBackToUpload}
                    data-testid="back-to-upload-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Upload Another
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={handleSave}
                      disabled={saving}
                      data-testid="save-changes-btn"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                    <Button
                      onClick={handlePrimaryAction}
                      disabled={creatingDraft || filing || (meta.primaryAction.kind === 'draft' && hasUnconfirmedLowConfidence)}
                      data-testid="create-draft-btn"
                    >
                      {(creatingDraft || filing) ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <FileCheck className="w-4 h-4 mr-2" />
                      )}
                      {meta.primaryAction.label}
                    </Button>
                  </div>
                </div>
                {hasUnconfirmedLowConfidence && meta.primaryAction.kind === 'draft' && (
                  <p className="text-xs text-amber-600 mt-2 text-right">
                    Please confirm all low-confidence fields before creating draft
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Duplicate Confirmation Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Potential Duplicate Detected
            </DialogTitle>
            <DialogDescription>
              {duplicateWarning?.message || 'A similar document may already exist.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to create this {meta.label.toLowerCase()} anyway?
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDuplicateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleCreateDraft(true)}
              disabled={creatingDraft}
              data-testid="confirm-create-anyway-btn"
            >
              {creatingDraft ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Create Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
